import {
  ExtendedFillLayer,
  ExtendedLineLayer,
  ExtendedRasterLayer,
  ExtendedSymbolLayer,
  LegendLayerConfig,
  MapLibreLayerSpecification,
} from '../types';
import { scaleOrdinal } from 'd3-scale';
import { schemeCategory10 } from 'd3-scale-chromatic';

// Create a color scale
const colorScale = scaleOrdinal(schemeCategory10);

/**
 * Helper function to determine if a value is a zoom-dependent expression
 * Checks if the value is an array where the first element is 'step' or 'interpolate'
 * and the second element is ['zoom']
 */
const isZoomDependentExpression = (value: any): boolean => {
  return Array.isArray(value) && 
         (value[0] === 'step' || value[0] === 'interpolate') && 
         Array.isArray(value[1]) && 
         value[1][0] === 'zoom';
};

/**
 * Transforms layer configuration and fetched data to MapLibre layer specification.
 * Take the layer definition, and also the fetched data for that layer with the proper Auth headers
 * TODO: Could we add the data to the LegendLayerConfig object?
 */
export const transformToMapLibreLayer = (
  layerConfig: LegendLayerConfig,
  data?: any // Optionally overwrite the url with fetched data
): MapLibreLayerSpecification | undefined => {
  // Example transformation logic for a GeoJSON layer
  if (layerConfig.type === "geojson") {
    if (layerConfig.geometry === "line") {
      return {
        id: layerConfig.id,
        type: "line",
        source: {
          type: "geojson",
          data: data || layerConfig.url,
        },
        paint: layerConfig.style,
        minzoom: layerConfig.options?.minZoom ?? 0,
        maxzoom: layerConfig.options?.maxZoom ?? 24,
      } as ExtendedLineLayer;
    } else if (layerConfig.geometry === "symbol") {
      // This condition checks if the layer has a default icon specified.
      // We only apply icon/text visibility settings for symbol layers that are intended to display icons.
      // Symbol layers without a defaultIcon might be text-only, so we skip applying these settings.
      if (layerConfig.defaultIcon) {
        const layout: any = {
          visibility: layerConfig.initialVisibility ? "visible" : "none",
          "icon-image": layerConfig.style.iconStyle || layerConfig.defaultIcon,
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "symbol-placement": "point",
          "symbol-z-order": "source",
          "icon-optional": false, // Never hide icons, even if text is placed
          "text-optional": true,  // Text can be hidden if needed
          "symbol-sort-key": 1,   // Higher priority for symbols
        };
        
        // Handle zoom-dependent icon size
        if (isZoomDependentExpression(layerConfig.style.iconSize)) {
          layout["icon-size"] = layerConfig.style.iconSize;
        } else {
          layout["icon-size"] = layerConfig.style.iconSize;
        }
        
        const paint: any = {};

        /** Implement Labels */
        if (layerConfig.style.labelField) {
          layout["text-field"] = layerConfig.style.labelField;
          
          // Handle zoom-dependent text size
          if (isZoomDependentExpression(layerConfig.style.labelSize)) {
            layout["text-size"] = layerConfig.style.labelSize;
          } else {
            layout["text-size"] = layerConfig.style.labelSize || 12;
          }
          
          // Handle zoom-dependent font
          if (isZoomDependentExpression(layerConfig.style.labelFont)) {
            layout["text-font"] = layerConfig.style.labelFont;
          } else {
            layout["text-font"] = layerConfig.style.labelFont || ["Mukta, sans-serif"];
          }
          
          // Handle zoom-dependent offset
          if (isZoomDependentExpression(layerConfig.style.labelOffset)) {
            layout["text-offset"] = layerConfig.style.labelOffset;
          } else {
            layout["text-offset"] = layerConfig.style.labelOffset || [1, 0];
          }
          
          // Handle zoom-dependent text color
          if (isZoomDependentExpression(layerConfig.style.labelColor)) {
            paint["text-color"] = layerConfig.style.labelColor;
          } else {
            paint["text-color"] = layerConfig.style.labelColor || "#ffffff";
          }
          
          // Handle optional text-opacity
          if (layerConfig.style.labelOpacity !== undefined) {
            if (isZoomDependentExpression(layerConfig.style.labelOpacity)) {
              paint["text-opacity"] = layerConfig.style.labelOpacity;
            } else {
              paint["text-opacity"] = layerConfig.style.labelOpacity;
            }
          }
          
          // Handle optional text-halo (outline)
          if (layerConfig.style.labelHaloColor !== undefined) {
            paint["text-halo-color"] = layerConfig.style.labelHaloColor;
            paint["text-halo-width"] = layerConfig.style.labelHaloWidth || 1;
          }
        }

        return {
          id: layerConfig.id,
          type: "symbol", // Change "point", "line", or "fill" to "symbol"
          source: {
            type: "geojson",
            data: data || layerConfig.url,
          },
          layout: layout,
          paint: paint,
          minzoom: layerConfig.options?.minZoom ?? 0,
          maxzoom: layerConfig.options?.maxZoom ?? 24,
        } as ExtendedSymbolLayer;
      }
    } else if (layerConfig.geometry === "fill" && layerConfig.styleByField) {
      let paint: any = {};
      let layout: any = {
        visibility: layerConfig.initialVisibility ? "visible" : "none",
      };

      // Collect unique names from the GeoJSON data
      const uniqueNames: Set<string> = new Set();
      let dataIsValid = false;

      if (data && data.features && Array.isArray(data.features) && data.features.length > 0) {
        dataIsValid = true;
        
        data.features.forEach((feature: any) => {
          if (feature.properties && feature.properties[layerConfig.styleByField!] !== undefined) {
            uniqueNames.add(feature.properties[layerConfig.styleByField!]);
          }
        });
      } else {
        // TODO: Sometimes we get to this point and the data property is still just referencing a url
      }
      
      const uniqueNamesArray = Array.from(uniqueNames); // Convert set to array if needed

      // Handle case where we have valid data with categories to style by
      if (dataIsValid && uniqueNamesArray.length > 0) {
        const colorExpressions = uniqueNamesArray.flatMap((name) => [
          ["==", ["get", layerConfig.styleByField!], name], // Condition
          colorScale(name), // Color value corresponding to the condition
        ]);
        
        paint = {
          "fill-color": [
            "case",
            ...colorExpressions,
            "#000000", // Default color
          ],
          "fill-opacity": 0.8,
          "fill-outline-color": "#000000",
        };
        
        // Apply custom styles if provided
        if ('opacity' in layerConfig.style) {
          paint["fill-opacity"] = layerConfig.style.opacity;
        }
        
        // Handle outline color if provided
        if ('fill-outline-color' in layerConfig.style) {
          paint["fill-outline-color"] = layerConfig.style["fill-outline-color" as keyof typeof layerConfig.style];
        }
      } else {
        // Fallback to a simple style when data isn't available yet
        paint = {
          "fill-color": "#4F1106", // Use a default color
          "fill-opacity": 0.8,
          "fill-outline-color": "#000000",
        };
        
        // Apply custom styles if provided
        if ('opacity' in layerConfig.style) {
          paint["fill-opacity"] = layerConfig.style.opacity;
        }
        
        // Handle outline color if provided
        if ('fill-outline-color' in layerConfig.style) {
          paint["fill-outline-color"] = layerConfig.style["fill-outline-color" as keyof typeof layerConfig.style];
        }
      }

      return {
        id: layerConfig.id,
        type: "fill",
        source: {
          type: "geojson",
          data: data || layerConfig.url,
        },
        layout: layout,
        paint: paint,
        minzoom: layerConfig.options?.minZoom ?? 0,
        maxzoom: layerConfig.options?.maxZoom ?? 24,
      } as ExtendedFillLayer; // Use a type assertion here
    } else if (layerConfig.geometry === "fill") {
      // Simple fill layer without styleByField
      return {
        id: layerConfig.id,
        type: "fill",
        source: {
          type: "geojson",
          data: data || layerConfig.url,
        },
        layout: {
          visibility: layerConfig.initialVisibility ? "visible" : "none",
        },
        paint: layerConfig.style,
        minzoom: layerConfig.options?.minZoom ?? 0,
        maxzoom: layerConfig.options?.maxZoom ?? 24,
      } as ExtendedFillLayer;
    }
    // Implement other cases (raster, raster-dem, etc.) based on your application's requirements

    return undefined; // Return null for unsupported or failed transformations
  } else if (layerConfig.type === "raster") {
    // Add WMS layers etc. (WMS layers are called type: raster in MapLibre GL JS)
    return {
      id: layerConfig.id,
      type: "raster",
      // @ts-ignore
      source: {
        type: "raster",
        tiles: [layerConfig.url],
        tileSize: layerConfig.options.tileSize || 256,
      },
      paint: {
        "raster-opacity": layerConfig.options.opacity || 1, // Use the opacity value from the layer's options object, or default to 1 if it's not set
      },
      layout: {
        visibility: layerConfig.initialVisibility ? "visible" : "none",
      },
    } as ExtendedRasterLayer;
  }
};
